import { useRef, useState } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ImageProcessor = ({ canvasRef }) => {
  const processImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        canvas.width = 990;
        canvas.height = 1089;
        const scale = Math.max(canvas.width / img.width, canvas.height / img.height);
        const x = (canvas.width - img.width * scale) / 2;
        const y = (canvas.height - img.height * scale) / 2;

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.9);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  return { processImage };
};

export const MainImageUpload = ({ mainImage, onImageUpdate, onImageRemove }) => {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700">Main Product Image</label>
      <div className="mt-1 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10"
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (file) onImageUpdate(file);
        }}
        onDragOver={(e) => e.preventDefault()}>
        <div className="text-center">
          {mainImage ? (
            <div className="relative inline-block">
              <img loading="lazy" 
                src={URL.createObjectURL(mainImage)}
                alt="Main product"
                className="w-40 h-44 object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={() => onImageRemove()}
                className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <PhotoIcon className="mx-auto h-12 w-12 text-gray-300" />
              <div className="mt-4 flex text-sm leading-6 text-gray-600">
                <label className="relative cursor-pointer rounded-md bg-white font-semibold text-orange-600">
                  <span>Upload main image</span>
                  <input
                    type="file"
                    className="sr-only"
                    onChange={(e) => e.target.files[0] && onImageUpdate(e.target.files[0])}
                    accept="image/*"
                  />
                </label>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const AdditionalImagesUpload = ({ additionalImages, onImagesUpdate, onImageRemove }) => {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700">Additional Product Images</label>
      <div className="mt-1 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10"
        onDrop={(e) => {
          e.preventDefault();
          const files = Array.from(e.dataTransfer.files);
          onImagesUpdate(files);
        }}
        onDragOver={(e) => e.preventDefault()}>
        <div className="text-center">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
            {additionalImages.map((img, index) => (
              <div key={index} className="relative inline-block">
                <img loading="lazy" 
                  src={URL.createObjectURL(img)}
                  alt={`Product ${index + 1}`}
                  className="w-32 h-35 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => onImageRemove(index)}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <PhotoIcon className="mx-auto h-12 w-12 text-gray-300" />
          <div className="mt-4 flex text-sm leading-6 text-gray-600">
            <label className="relative cursor-pointer rounded-md bg-white font-semibold text-orange-600">
              <span>Upload additional images</span>
              <input
                type="file"
                multiple
                className="sr-only"
                onChange={(e) => onImagesUpdate(Array.from(e.target.files))}
                accept="image/*"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export const useImageHandling = () => {
  const canvasRef = useRef(null);
  const [mainImage, setMainImage] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const { processImage } = ImageProcessor({ canvasRef });

  const handleMainImageUpdate = async (file) => {
    const processedFile = await processImage(file);
    setMainImage(processedFile);
  };

  const handleAdditionalImagesUpdate = async (files) => {
    const processedFiles = await Promise.all(files.map(processImage));
    setAdditionalImages(prev => [...prev, ...processedFiles]);
  };

  const handleMainImageRemove = () => setMainImage(null);

  const handleAdditionalImageRemove = (index) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const getAllImages = () => {
    const images = [];
    if (mainImage) {
      images.push(mainImage);
    }
    if (additionalImages.length > 0) {
      images.push(...additionalImages);
    }
    return images;
  };

  return {
    canvasRef,
    mainImage,
    additionalImages,
    handleMainImageUpdate,
    handleAdditionalImagesUpdate,
    handleMainImageRemove,
    handleAdditionalImageRemove,
    getAllImages, // Add the new function to the returned object
  };
};